import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Minus, Plus, ChevronLeft, ChevronRight, ShoppingCart, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

// Choose Variant bottom sheet — weight-swipe + per-flavour counters.
// Header order:  [Size · Choose Flavour] → [Name] → [₹Price] → [Image] → [Flavour list]
// Footer:        [Show Cart]              [Confirm]
export default function VariantSheet({
  open,
  onOpenChange,
  products = [],
  initialProduct,
  size,
  sizes = [],
  categoryName = "",
}) {
  const { addItem } = useCart();

  const orderedSizes = sizes && sizes.length ? sizes : (size ? [size] : []);
  const [activeSize, setActiveSize] = useState(size || orderedSizes[0] || null);
  const [selections, setSelections] = useState({}); // { `${productId}:${size}` : {product, variant, size, qty} }
  const [focusedProductId, setFocusedProductId] = useState(null); // used to sync the header preview
  const [showSummary, setShowSummary] = useState(false);
  const scrollRef = useRef(null);
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);

  // Reset when the sheet re-opens.
  useEffect(() => {
    if (!open) return;
    setActiveSize(size || orderedSizes[0] || null);
    setSelections({});
    setFocusedProductId(initialProduct?.id || null);
    setShowSummary(false);
    // Pre-select the searched product with qty=1 for the current size, if applicable.
    if (initialProduct) {
      const targetSize = size || orderedSizes[0];
      const v = (initialProduct.variants || []).find((x) => x.label === targetSize);
      if (v) {
        setSelections({
          [`${initialProduct.id}:${targetSize}`]: { product: initialProduct, variant: v, size: targetSize, qty: 1 },
        });
      }
    }
  }, [open]);

  // Flavour list for the current weight.
  const currentFlavours = useMemo(() => {
    if (!activeSize) return [];
    return products
      .map((p) => ({ product: p, variant: p.variants.find((v) => v.label === activeSize) }))
      .filter((x) => !!x.variant);
  }, [products, activeSize]);

  // Header preview product = focused (if it has this weight) else first flavour.
  const headerProduct = useMemo(() => {
    const focused = currentFlavours.find((f) => f.product.id === focusedProductId);
    return focused || currentFlavours[0] || null;
  }, [currentFlavours, focusedProductId]);

  // Reset scroll when swiping weights.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activeSize]);

  const keyFor = (pid, sz) => `${pid}:${sz}`;

  const setQty = (product, variant, sz, qty) => {
    const k = keyFor(product.id, sz);
    setSelections((prev) => {
      const next = { ...prev };
      const n = Math.max(0, Math.min(999, Math.floor(Number(qty) || 0)));
      if (n === 0) {
        delete next[k];
      } else {
        next[k] = { product, variant, size: sz, qty: n };
      }
      return next;
    });
  };

  const bumpQty = (product, variant, sz, delta) => {
    const k = keyFor(product.id, sz);
    const cur = selections[k]?.qty || 0;
    setQty(product, variant, sz, cur + delta);
  };

  // Swipe left/right to cycle weights.
  const canPrev = orderedSizes.indexOf(activeSize) > 0;
  const canNext = orderedSizes.indexOf(activeSize) >= 0 && orderedSizes.indexOf(activeSize) < orderedSizes.length - 1;
  const goPrev = () => {
    const i = orderedSizes.indexOf(activeSize);
    if (i > 0) setActiveSize(orderedSizes[i - 1]);
  };
  const goNext = () => {
    const i = orderedSizes.indexOf(activeSize);
    if (i >= 0 && i < orderedSizes.length - 1) setActiveSize(orderedSizes[i + 1]);
  };

  const onTouchStart = (e) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (swipeStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - swipeStartX.current;
    const dy = e.changedTouches[0].clientY - swipeStartY.current;
    swipeStartX.current = null;
    swipeStartY.current = null;
    // Horizontal swipe threshold + must be predominantly horizontal.
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  const selectionList = useMemo(() => Object.values(selections), [selections]);
  const totalQty = selectionList.reduce((s, x) => s + x.qty, 0);
  const grandTotal = selectionList.reduce((s, x) => s + x.qty * x.variant.price, 0);

  const confirm = () => {
    if (selectionList.length === 0) {
      toast.error("Add at least one flavour before confirming");
      return;
    }
    // Preserve current scroll position so returning to Home stays in place.
    const scrollY = window.scrollY;
    selectionList.forEach((s) => {
      addItem({
        product_id: s.product.id,
        product_name: s.product.name,
        variant_id: s.variant.id,
        variant_label: s.variant.label,
        image_url: s.variant.image_url || s.product.image_url,
        unit_price: s.variant.price,
        quantity: s.qty,
      });
    });
    toast.success(`Added ${totalQty} item${totalQty === 1 ? "" : "s"} to cart · ₹${grandTotal}`, { duration: 2500 });
    onOpenChange(false);
    // Small microtask defer — restore scroll after dialog close animation.
    requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" in window ? "instant" : "auto" }));
  };

  if (!activeSize || currentFlavours.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="variant-sheet"
        className="fixed inset-x-0 bottom-0 top-auto left-0 right-0 translate-x-0 translate-y-0 max-w-full w-full sm:max-w-2xl md:max-w-4xl lg:max-w-5xl sm:mx-auto sm:inset-x-4 sm:bottom-4 rounded-t-2xl sm:rounded-2xl bg-ivory border-0 p-0 h-[94vh] sm:h-auto sm:max-h-[94vh] md:h-[86vh] overflow-hidden flex flex-col data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <DialogTitle className="sr-only">Choose a variant</DialogTitle>
        <DialogDescription className="sr-only">
          Pick flavours available in {activeSize}. Swipe left/right to change weight.
        </DialogDescription>

        {/* Drag handle */}
        <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
          <span className="w-10 h-1.5 bg-brown-border rounded-full" />
        </div>

        {/* HEADER — Size · Choose Flavour  → Name → ₹Price → Image */}
        <div className="flex-shrink-0 bg-ivory px-5 md:px-6 pt-3 pb-3 border-b border-brown-border/70 relative">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-brand-red font-bold mb-1">
                <button
                  onClick={goPrev}
                  disabled={!canPrev}
                  data-testid="weight-prev"
                  className="w-6 h-6 rounded-full inline-flex items-center justify-center text-brand-red hover:bg-brand-red/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous weight"
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={3} />
                </button>
                <span data-testid="active-size-label">{activeSize} · Choose Flavour</span>
                <button
                  onClick={goNext}
                  disabled={!canNext}
                  data-testid="weight-next"
                  className="w-6 h-6 rounded-full inline-flex items-center justify-center text-brand-red hover:bg-brand-red/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next weight"
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={3} />
                </button>
              </div>
              {headerProduct && (
                <>
                  <h2 className="text-brown text-2xl font-extrabold leading-tight pr-10">
                    {headerProduct.product.name}
                  </h2>
                  <div className="text-brown font-bold text-lg mt-0.5">
                    ₹{headerProduct.variant.price}
                  </div>
                  {headerProduct.product.description && (
                    <p
                      data-testid="variant-sheet-description"
                      className="text-brown-medium text-xs md:text-sm mt-1.5 leading-relaxed pr-10 whitespace-pre-line"
                    >
                      {headerProduct.product.description}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Weight dots — quick indicator */}
          <div className="mt-2 flex items-center gap-1.5">
            {orderedSizes.map((sz) => (
              <button
                key={sz}
                data-testid={`size-dot-${sz}`}
                onClick={() => setActiveSize(sz)}
                className={`h-1.5 rounded-full transition-all ${sz === activeSize ? "w-6 bg-brand-red" : "w-1.5 bg-brown-border"}`}
                aria-label={`Show ${sz}`}
              />
            ))}
          </div>
        </div>

        {/* MIDDLE — mobile stays stacked (image above list, list scrolls).
            Desktop (≥ md) switches to a 60/40 side-by-side layout: the pack
            image dominates the left column, flavours scroll on the right.
            Image uses `object-contain` on desktop so every product (bottle,
            jar, pouch, packet) is fully visible, centered, and never cropped
            or stretched. A blurred duplicate of the same image sits behind
            it to fill the letterbox area with a premium tinted backdrop. */}
        <div className="flex-1 min-h-0 flex flex-col md:flex-row md:overflow-hidden">
          {/* IMAGE column */}
          {headerProduct && (
            <div className="relative flex-shrink-0 h-[26vh] max-h-[220px] sm:h-[38vh] sm:max-h-[380px] md:flex-shrink md:h-auto md:max-h-none md:w-3/5 md:min-h-0 bg-ivory-soft overflow-hidden flex items-center justify-center">
              <img
                aria-hidden="true"
                src={headerProduct.variant.image_url || headerProduct.product.image_url}
                alt=""
                className="hidden sm:block absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40 pointer-events-none"
              />
              <img
                src={headerProduct.variant.image_url || headerProduct.product.image_url}
                alt={headerProduct.product.name}
                loading="lazy"
                decoding="async"
                className="relative z-[1] w-full h-full object-cover object-center scale-[1.05] sm:object-contain sm:scale-100 sm:max-w-[92%] sm:max-h-[92%] md:max-w-[94%] md:max-h-[94%] transition-opacity duration-300"
                data-testid="variant-sheet-image"
              />
            </div>
          )}

          {/* FLAVOUR LIST column — scrolls independently on desktop */}
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 py-3 md:w-2/5 md:border-l md:border-brown-border/60">
            <div className="space-y-2">
            {currentFlavours.map(({ product: p, variant: v }) => {
              const k = keyFor(p.id, activeSize);
              const qty = selections[k]?.qty || 0;
              return (
                <div
                  key={p.id}
                  data-testid={`flavour-row-${p.id}`}
                  onClick={() => setFocusedProductId(p.id)}
                  className={`bg-white border rounded-xl p-3 flex items-center gap-3 transition-colors ${
                    qty > 0 ? "border-brand-red shadow-[0_1px_0_0_rgba(200,42,42,0.15)]" : "border-brown-border"
                  }`}
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-ivory-soft flex-shrink-0">
                    <img
                      src={v.image_url || p.image_url}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-brown font-bold text-sm md:text-[15px] leading-tight truncate">
                      {p.name}
                    </div>
                    <div className="text-brown-medium text-xs mt-0.5">₹{v.price}</div>
                  </div>

                  {qty === 0 ? (
                    <button
                      data-testid={`flavour-add-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); bumpQty(p, v, activeSize, 1); }}
                      className="h-9 px-4 rounded-full bg-brown text-ivory text-[11px] font-bold tracking-widest uppercase hover:bg-brand-red transition-colors"
                    >
                      Add
                    </button>
                  ) : (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 bg-brand-red text-ivory rounded-full h-9 px-1"
                    >
                      <button
                        data-testid={`flavour-dec-${p.id}`}
                        onClick={() => bumpQty(p, v, activeSize, -1)}
                        className="w-7 h-7 rounded-full inline-flex items-center justify-center hover:bg-brand-red-dark transition-colors"
                        aria-label="Decrease"
                      >
                        <Minus className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                      <input
                        data-testid={`flavour-qty-input-${p.id}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={999}
                        value={qty}
                        onClick={(e) => e.target.select()}
                        onChange={(e) => setQty(p, v, activeSize, e.target.value)}
                        onBlur={(e) => {
                          if (!e.target.value) setQty(p, v, activeSize, 0);
                        }}
                        className="w-11 text-center bg-transparent outline-none text-ivory font-bold text-sm tabular-nums appearance-none"
                        style={{ MozAppearance: "textfield" }}
                      />
                      <button
                        data-testid={`flavour-inc-${p.id}`}
                        onClick={() => bumpQty(p, v, activeSize, 1)}
                        className="w-7 h-7 rounded-full inline-flex items-center justify-center hover:bg-brand-red-dark transition-colors"
                        aria-label="Increase"
                      >
                        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>

        {/* FOOTER — Show Cart · Confirm */}
        <div className="flex-shrink-0 bg-ivory border-t border-brown-border/70 px-4 md:px-5 py-3">
          <div className="flex items-stretch gap-2">
            <button
              data-testid="variant-show-cart-btn"
              onClick={() => setShowSummary(true)}
              className="flex-1 h-12 rounded-full border-2 border-brown text-brown font-bold text-[12px] tracking-[0.14em] uppercase hover:bg-brown hover:text-ivory transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <ShoppingCart className="w-4 h-4" />
              Show Cart {totalQty > 0 && <span className="ml-1 bg-brand-red text-ivory text-[10px] font-bold px-1.5 py-0.5 rounded-full">{totalQty}</span>}
            </button>
            <button
              data-testid="variant-confirm-btn"
              onClick={confirm}
              disabled={selectionList.length === 0}
              className="flex-1 h-12 rounded-full bg-brand-red text-ivory font-bold text-[12px] tracking-[0.14em] uppercase hover:bg-brand-red-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm {grandTotal > 0 && <span className="ml-1">· ₹{grandTotal}</span>}
            </button>
          </div>
        </div>

        {/* SHOW-CART SUMMARY OVERLAY (in-sheet) */}
        {showSummary && (
          <CartSummaryOverlay
            selections={selectionList}
            totalQty={totalQty}
            grandTotal={grandTotal}
            categoryName={categoryName}
            onSetQty={setQty}
            onBump={bumpQty}
            onDelete={(product, sz) => setQty(product, null, sz, 0)}
            onClose={() => setShowSummary(false)}
            onConfirm={() => { setShowSummary(false); confirm(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CartSummaryOverlay({ selections, totalQty, grandTotal, categoryName, onSetQty, onBump, onDelete, onClose, onConfirm }) {
  return (
    <div
      data-testid="cart-summary-overlay"
      className="absolute inset-0 bg-ivory z-10 flex flex-col animate-in fade-in duration-150"
    >
      <div className="pt-2 pb-1 flex justify-center flex-shrink-0">
        <span className="w-10 h-1.5 bg-brown-border rounded-full" />
      </div>
      <div className="flex-shrink-0 px-5 md:px-6 pt-2 pb-4 border-b border-brown-border/70">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-red font-bold">Your Selection</div>
            <h3 className="text-brown text-xl font-extrabold leading-tight">
              {categoryName ? `${categoryName} · Cart Preview` : "Cart Preview"}
            </h3>
          </div>
          {selections.length > 0 && (
            <div
              data-testid="floating-cart-pill"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-brand-red text-ivory rounded-full h-10 pl-3 pr-4 shadow-[0_4px_12px_-2px_rgba(200,42,42,0.4)]"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              <span data-testid="floating-cart-items" className="text-[12px] font-bold tabular-nums">{totalQty} {totalQty === 1 ? "Item" : "Items"}</span>
              <span className="opacity-60">•</span>
              <span data-testid="floating-cart-total" className="text-[13px] font-extrabold tabular-nums">₹{grandTotal}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 md:px-5 py-4">
        {selections.length === 0 ? (
          <div className="text-center py-12 text-brown-medium">
            <p className="text-sm">No flavours selected yet.</p>
            <p className="text-xs mt-1">Close this and tap Add on any flavour.</p>
          </div>
        ) : (
          <div className="bg-white border border-brown-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-ivory-warm text-brown-medium text-[9px] tracking-[0.12em] uppercase">
                    <th className="text-left px-1.5 py-2">Flavour</th>
                    <th className="text-left px-1 py-2">Size</th>
                    <th className="text-center px-1 py-2">Qty</th>
                    <th className="text-right px-1 py-2">Price</th>
                    <th className="text-right px-1.5 py-2">Subtotal</th>
                  </tr>
                </thead>
                <tbody data-testid="summary-rows">
                  {selections.map((s) => (
                    <CartSummaryRow
                      key={`${s.product.id}:${s.size}`}
                      s={s}
                      onSetQty={onSetQty}
                      onBump={onBump}
                      onDelete={onDelete}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-ivory-warm border-t-2 border-brown-border">
                    <td className="px-1.5 py-2.5 text-brown font-bold text-[10px] tracking-widest uppercase" colSpan={2}>Total</td>
                    <td data-testid="summary-total-qty" className="px-1 py-2.5 text-brown font-bold text-center tabular-nums">{totalQty}</td>
                    <td className="px-1 py-2.5"></td>
                    <td data-testid="summary-grand-total" className="px-1.5 py-2.5 text-brand-red font-extrabold text-right tabular-nums">₹{grandTotal}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 border-t border-brown-border/70 px-4 md:px-5 py-3 flex items-stretch gap-2">
        <button
          data-testid="summary-back-btn"
          onClick={onClose}
          className="flex-1 h-12 rounded-full border-2 border-brown text-brown font-bold text-[12px] tracking-[0.14em] uppercase hover:bg-brown hover:text-ivory transition-colors"
        >
          Close Cart
        </button>
        <button
          data-testid="summary-confirm-btn"
          onClick={onConfirm}
          disabled={selections.length === 0}
          className="flex-1 h-12 rounded-full bg-brand-red text-ivory font-bold text-[12px] tracking-[0.14em] uppercase hover:bg-brand-red-dark transition-colors disabled:opacity-40"
        >
          Confirm · ₹{grandTotal}
        </button>
      </div>
    </div>
  );
}

function CartSummaryRow({ s, onSetQty, onBump, onDelete }) {
  // Local input state — allow empty typing without wiping the row.
  // On blur: if empty/invalid → restore previous qty; else commit.
  const [local, setLocal] = React.useState(String(s.qty));
  React.useEffect(() => { setLocal(String(s.qty)); }, [s.qty]);
  const subtotal = s.qty * s.variant.price;
  return (
    <tr data-testid={`summary-row-${s.product.id}-${s.size}`} className="border-t border-brown-border/60 align-middle">
      <td className="px-1.5 py-2 text-brown font-semibold">
        <div className="truncate max-w-[92px] md:max-w-[220px]" title={s.product.name}>{s.product.name}</div>
      </td>
      <td className="px-1 py-2 text-brown-medium tabular-nums text-[12px]">{s.size}</td>
      <td className="px-1 py-2">
        <div className="inline-flex items-center gap-0 bg-brand-red text-ivory rounded-full h-7">
          <button
            data-testid={`summary-dec-${s.product.id}-${s.size}`}
            onClick={() => onBump(s.product, s.variant, s.size, -1)}
            className="w-6 h-7 rounded-full inline-flex items-center justify-center hover:bg-brand-red-dark transition-colors"
            aria-label="Decrease"
          >
            <Minus className="w-3 h-3" strokeWidth={3} />
          </button>
          <input
            data-testid={`summary-qty-input-${s.product.id}-${s.size}`}
            type="number"
            inputMode="numeric"
            min={1}
            max={999}
            value={local}
            onFocus={(e) => { setLocal(""); e.target.select(); }}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => {
              const n = parseInt(local, 10);
              if (isNaN(n) || n <= 0) {
                setLocal(String(s.qty));   // blank → restore previous
              } else {
                onSetQty(s.product, s.variant, s.size, n);
              }
            }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            className="w-10 text-center bg-transparent outline-none text-ivory font-bold text-[13px] tabular-nums appearance-none"
            style={{ MozAppearance: "textfield" }}
          />
          <button
            data-testid={`summary-inc-${s.product.id}-${s.size}`}
            onClick={() => onBump(s.product, s.variant, s.size, 1)}
            className="w-6 h-7 rounded-full inline-flex items-center justify-center hover:bg-brand-red-dark transition-colors"
            aria-label="Increase"
          >
            <Plus className="w-3 h-3" strokeWidth={3} />
          </button>
        </div>
      </td>
      <td className="px-1 py-2 text-brown-medium text-right tabular-nums text-[12px]">₹{s.variant.price}</td>
      <td className="px-1.5 py-2 text-right whitespace-nowrap">
        <span data-testid={`summary-subtotal-${s.product.id}-${s.size}`} className="text-brown font-bold tabular-nums">₹{subtotal}</span>
        <button
          data-testid={`summary-delete-${s.product.id}-${s.size}`}
          onClick={() => onDelete(s.product, s.size)}
          className="ml-1 w-6 h-6 rounded-full inline-flex items-center justify-center text-brown-medium hover:text-brand-red hover:bg-brand-red/10 transition-colors align-middle"
          aria-label={`Delete ${s.product.name}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </td>
    </tr>
  );
}
